<script setup lang="ts">
import { computed } from 'vue';
import { isNilOrBlankString } from '@nova/core';
import { useCustomFieldProps } from '@satellite/composables/useCustomFieldProps';
import { useCustomFieldValidation } from '@satellite/composables/useCustomFieldValidation';
import { customFieldInputTestId } from '@satellite/helpers/customFieldTestId';

import type { CustomFieldProps } from '../types';
import CustomFieldInputBase from '../CustomFieldInputBase.vue';

/**
 * Number field type - numeric input
 */
const {
  hideDetails = 'auto',
  displayLabel = true,
  persistentPlaceholder = true,
  required = undefined,
  ...props
} = defineProps<CustomFieldProps>();

const emit = defineEmits<{
  'update:model-value': [value: number | ''];
  /** @deprecated Use update:model-value instead */
  input: [value: number | ''];
}>();

const { normalizedProps } = useCustomFieldProps(() => ({
  ...props,
  hideDetails,
  displayLabel,
  persistentPlaceholder,
  required
}));
const { rules } = useCustomFieldValidation(
  computed(() => ({ ...props.field, required: normalizedProps.required }))
);

/**
 * The `int` type is a whole number, so the keys a type="number" input accepts for decimals and
 * exponents are blocked at the keyboard, as Helios's number field did. The comma covers locales
 * where the browser takes it as the decimal separator. `-` stays, since negatives are allowed.
 * A pasted decimal still gets through; the whole-number validation rule reports that one.
 */
const NON_INTEGER_KEYS = ['e', 'E', '+', '.', ','];

function handleKeydown(event: KeyboardEvent) {
  if (NON_INTEGER_KEYS.includes(event.key)) {
    event.preventDefault();
  }
}

/**
 * A blank field emits '' rather than a number: Number('') and Number(null) are both 0, so a
 * cleared optional field would otherwise save as zero. Typing emits '' when the input empties,
 * while a parent v-form's reset() sets the model to null.
 */
function handleInput(value: string | null) {
  const numericValue = isNilOrBlankString(value) ? '' : Number(value);
  emit('update:model-value', numericValue);
  emit('input', numericValue);
}
</script>

<template>
  <custom-field-input-base
    :field="field"
    v-bind="normalizedProps"
    :data-testid="customFieldInputTestId(testId, field.label)">
    <v-text-field
      color="primary"
      density="comfortable"
      inputmode="numeric"
      type="number"
      variant="outlined"
      :hide-details="normalizedProps.hideDetails"
      :model-value="modelValue"
      :persistent-placeholder="normalizedProps.persistentPlaceholder"
      :placeholder="field.placeholder"
      :readonly="normalizedProps.readOnly"
      :rules="rules"
      @keydown="handleKeydown"
      @update:model-value="handleInput" />
  </custom-field-input-base>
</template>
